import { useState } from 'react';
import { X, Check, Folder } from '../../icons';
import { Button } from '../../components/shared';
import type { NodePublic } from '../../api/workspace';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useEscapeClose } from '../../hooks/useEscapeClose';
import { tWorkspace } from '../../lib/workspace-i18n';

/**
 * 폴더 색상 (2026-08-14) — 행 메뉴의 "폴더 색상".
 *
 * 자유 입력을 주지 않고 **팔레트에서만** 고르게 한다. 서버 스키마도 `#rrggbb` 만 받는데
 * (자유 문자열을 허용하면 `url(...)` 같은 값이 style 에 실려 CSS injection 이 된다),
 * 입력창을 두면 사용자가 거절당하는 값을 넣을 여지가 생긴다.
 */

/** 앱 토큰과 어울리는 8색. 마지막은 "색 없음"(기본)으로 되돌리는 자리. */
const PALETTE = [
  '#3b82f6', '#8b5cf6', '#ec4899', '#ef4444',
  '#f59e0b', '#10b981', '#0ea5e9', '#64748b',
] as const;

interface Props {
  node: NodePublic;
  onClose: () => void;
  onApply: (color: string | null) => void | Promise<void>;
}

export function WorkspaceColorModal({ node, onClose, onApply }: Props) {
  const trapRef = useFocusTrap<HTMLDivElement>(true);
  const [picked, setPicked] = useState<string | null>(node.color ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscapeClose(onClose, busy);

  async function apply() {
    setBusy(true);
    setError(null);
    try {
      await onApply(picked);
    } catch (err) {
      setError((err as any)?.response?.data?.message ?? (err as Error).message);
      setBusy(false);
    }
  }

  return (
    <div
      className="workspace-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={tWorkspace('workspace.row.color')}
    >
      <div ref={trapRef} className="workspace-modal ws-color-modal">
        <header className="workspace-modal-header">
          <h2>{tWorkspace('workspace.color.heading', { name: node.name })}</h2>
          <Button variant="ghost" size="sm" iconOnly
            onClick={onClose} disabled={busy} aria-label={tWorkspace('workspace.modal.aria_close')}
            iconLeft={<X size={18} />}
          />
        </header>

        <div className="workspace-modal-body">
          <div className="ws-color-preview">
            <Folder size={34} aria-hidden style={picked ? { color: picked } : undefined} />
            <span>{node.name}</span>
          </div>

          <div className="ws-swatches" role="radiogroup" aria-label={tWorkspace('workspace.color.aria')}>
            {PALETTE.map((c, i) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={picked === c}
                aria-label={tWorkspace('workspace.color.swatch', { n: i + 1 })}
                className={`ws-swatch${picked === c ? ' ws-swatch--on' : ''}`}
                style={{ background: c }}
                onClick={() => setPicked(c)}
              >
                {picked === c && <Check size={14} aria-hidden />}
              </button>
            ))}
            <button
              type="button"
              role="radio"
              aria-checked={picked === null}
              className={`ws-swatch ws-swatch--none${picked === null ? ' ws-swatch--on' : ''}`}
              onClick={() => setPicked(null)}
            >
              {tWorkspace('workspace.color.none')}
            </button>
          </div>

          {error && <div className="workspace-modal-error" role="alert">{error}</div>}

          <footer className="workspace-modal-footer">
            <Button variant="secondary"
              onClick={onClose} disabled={busy}
            >
              {tWorkspace('workspace.modal.btn_cancel')}
            </Button>
            <Button variant="primary"
              onClick={() => void apply()} disabled={busy}
            >
              {tWorkspace('workspace.color.apply')}
            </Button>
          </footer>
        </div>
      </div>
    </div>
  );
}
